import { ImageAnnotation, ImageAnnotator, useAnnotator } from "@annotorious/react"
import { DettaglioTavola, Tavola } from "../../types"
import "@annotorious/react/annotorious-react.css"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { annotationToCoords, dettaglioTavolaToAnnotation, hasCoords } from "../../utils/annotations"
import { Button, H4 } from "@inmagik/magik-ui"
import { useDropDetailCoords, useUpdateDetailsCoords } from "../../hooks/tavole"
import { Collapse } from "react-bootstrap"
import useModalTrigger from "magik-react-hooks/useModalTrigger"
import ModalDelete from "../../components/ModalDelete"

type TavolaMappaProps = {
  tavola: Tavola
  dettagliTavola?: DettaglioTavola[]
  editDetail: (dettaglio: DettaglioTavola) => void
  deleteDetail: (dettaglio: DettaglioTavola) => void
  refetch: () => void
}

export default function TavolaMappa({ tavola, dettagliTavola, editDetail, deleteDetail, refetch }: TavolaMappaProps) {
  const [detail, _setDetail] = useState<number | null>(null)

  const detailRef = useRef<number | null>(null)

  const annotator = useAnnotator()

  const dropDetailCoords = useDropDetailCoords(tavola?.id || undefined)
  const [modalDropDetailCoordsState, modalDropDetailCoordsActions] = useModalTrigger()

  const updateDetailsCoords = useUpdateDetailsCoords(tavola.id)

  const setDetail = useCallback((id: number | null) => {
    _setDetail(id)
    detailRef.current = id
  }, [])

  const mouseState = useRef<"up" | "down" | null>(null)

  const setDetailValue = useCallback(
    (annotation: any) => {
      const nextAnnotation = {
        ...annotation,
        properties: {
          ...annotation.properties,
          id: detailRef.current,
        },
      }
      annotator.updateAnnotation(nextAnnotation)

      const coords = annotationToCoords(nextAnnotation)
      if (coords) {
        const changesLocal = {
          [detailRef.current as number]: {
            top: coords.top,
            left: coords.left,
            bottom: coords.bottom,
            right: coords.right,
          },
        }
        const payload = Object.keys(changesLocal).map((key) => ({
          ...changesLocal[Number(key)],
          id: Number(key),
        }))

        if (!payload || payload.length === 0) return
        if (mouseState.current !== "up") return

        updateDetailsCoords.mutateAsync(payload).then((resp) => {
          mouseState.current = null
          refetch()
        })
      }
    },
    [annotator, updateDetailsCoords, refetch]
  )

  const updateDetailValue = useCallback((updated: any, previous: any) => {
    const coords = annotationToCoords(updated)

    if (detailRef.current && coords) {
      const changesLocal = {
        [detailRef.current as number]: {
          top: coords.top,
          left: coords.left,
          bottom: coords.bottom,
          right: coords.right,
        },
      }

      const payload = Object.keys(changesLocal).map((key) => ({
        ...changesLocal[Number(key)],
        id: Number(key),
      }))

      if (!payload || payload.length === 0) return
      if (mouseState.current !== "up") return

      updateDetailsCoords.mutateAsync(payload).then((resp) => {
        mouseState.current = null
        refetch()
      })
    }
  }, [])

  useEffect(() => {
    if (!annotator) return
    annotator.on("createAnnotation", setDetailValue)
    annotator.on("updateAnnotation", updateDetailValue)

    return () => {
      annotator.off("createAnnotation", setDetailValue)
      annotator.off("updateAnnotation", updateDetailValue)
    }
  }, [annotator, setDetailValue, updateDetailValue])

  useEffect(() => {
    if (!annotator || !dettagliTavola) return
    const nextAnnotations: ImageAnnotation[] = []
    dettagliTavola.forEach((dettaglio) => {
      const annotation = dettaglioTavolaToAnnotation(dettaglio)
      if (annotation) {
        nextAnnotations.push(annotation)
      }
    })
    // Set annotations
    const currentAnnotations = annotator.getAnnotations() as ImageAnnotation[]
    const idsToKeep: number[] = []
    nextAnnotations.forEach((annotation) => {
      idsToKeep.push(annotation.properties?.id as number)
      const exists = currentAnnotations.find((a) => a.properties?.id === annotation.properties?.id)
      if (!exists) {
        annotator.addAnnotation(annotation)
      }
    })

    // Remove annotations that are no longer present
    currentAnnotations.forEach((annotation) => {
      if (!idsToKeep.includes(annotation.properties?.id as number)) {
        annotator.removeAnnotation(annotation)
      }
    })
  }, [annotator, dettagliTavola])

  const drawEnabled = useMemo(() => {
    const detailItem = dettagliTavola?.find((d) => d.id === detail)
    if (!detailItem) return false
    return detail !== null && !hasCoords(detailItem)
  }, [detail, dettagliTavola])

  useEffect(() => {
    if (!annotator) return
    annotator.setStyle((annotation, state) => {
      // If the annotation has an 'important' label, make it red
      const color = annotation.properties?.id.toString() === detail?.toString() ? "#ff0000" : "#ffffff"

      return {
        fill: color,
        stroke: "#ff0000",
        strokeOpacity: 1,
      }
    })
  }, [annotator, detail])

  useEffect(() => {
    if (!annotator) return

    function onSelectAnnotation(annotations: unknown[]) {
      if (!Array.isArray(annotations) || annotations.length === 0) {
        // setDetail(null)
        return
      }
      const annotation = annotations[0] as ImageAnnotation
      const id = Number(annotation.properties?.id)
      if (Number.isInteger(id)) {
        setDetail(id)
      }
    }

    annotator.on("selectionChanged", onSelectAnnotation)

    return () => {
      annotator.off("selectionChanged", onSelectAnnotation)
    }
  }, [annotator, detail])

  return (
    <div className="row h-100">
      <div
        className="col-8 h-100"
        onMouseDown={() => {
          mouseState.current = "down"
        }}
        onMouseUp={() => {
          mouseState.current = "up"
        }}
      >
        <ImageAnnotator
          drawingEnabled={drawEnabled}
          autoSave={true}
          tool={"rectangle"}
          drawingMode={"drag"}
          containerClassName="w-100 h-100"
        >
          <div className="position-relative h-100 w-100">
            <div className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-dark-subtle">
              <img
                src={tavola.immagine_principale_data.immagine}
                style={{ width: "100%", height: "100%", objectFit: "contain" }}
              />
            </div>
          </div>
        </ImageAnnotator>
      </div>
      <div className="col-4 h-100 overflow-auto">
        <div className="d-flex flex-column align-items-start mb-2 bg-primary p-2 rounded">
          <div className="bg-primary text-white">Dettagli Tavola</div>
        </div>
        <div className="px-2">
          <p className="smaller-text">Seleziona un dettaglio per disegnarlo o modificarlo</p>
        </div>

        {(dettagliTavola || []).map((dettaglio) => (
          <DettaglioItem
            key={dettaglio.id}
            dettaglio={dettaglio}
            detail={detail}
            setDetail={setDetail}
            editDetail={editDetail}
            deleteDetail={deleteDetail}
            removeDetailCoords={(id) => {
              modalDropDetailCoordsActions.open(id)
            }}
          />
        ))}
      </div>

      {modalDropDetailCoordsState.isOpen && (
        <ModalDelete
          isOpen={modalDropDetailCoordsState.isOpen}
          onClose={modalDropDetailCoordsActions.close}
          onDelete={() => {
            return dropDetailCoords.mutateAsync({ dettaglio: modalDropDetailCoordsState.value }).then((resp) => {
              modalDropDetailCoordsActions.close()
              refetch()
            })
          }}
          message="Sei sicuro di voler eliminare la posizione del dettaglio"
          title="Elimina posizione dettaglio"
        />
      )}
    </div>
  )
}

function DettaglioItem({
  dettaglio,
  detail,
  setDetail,
  editDetail,
  deleteDetail,
  removeDetailCoords,
}: {
  dettaglio: DettaglioTavola
  detail: null | number
  setDetail: (id: number | null) => void
  editDetail: (dettaglio: DettaglioTavola) => void
  deleteDetail: (dettaglio: DettaglioTavola) => void
  removeDetailCoords: (id: number) => void
}) {
  return (
    <div
      className={`card mb-2 ${detail === dettaglio.id ? "bg-primary-subtle" : ""}`}
      key={dettaglio.id}
      onClick={() => setDetail(dettaglio.id)}
      style={{ cursor: "pointer" }}
    >
      <div className="card-body">
        <div className="d-flex flex-row align-items-center justify-content-between">
          <h6 className="card-title flex-grow-1 d-flex align-items-center gap-1">
            <div
              className={`d-flex flex-row align-items-center ${hasCoords(dettaglio) ? "text-success" : "text-danger"}`}
            >
              <i
                className="material-symbols-rounded"
                style={{
                  fontSize: 16,
                  fontVariationSettings: '"FILL" 1, "wght" 400, "GRAD" 0, "opsz" 48',
                }}
              >
                circle
              </i>
            </div>
            {dettaglio.titolo}
          </h6>
          <div
            className="d-flex flex-row align-items-center gap-2"
            style={{ opacity: detail === dettaglio.id ? 1 : 0 }}
          >
            {hasCoords(dettaglio) && (
              <Button leadingIcon="close" variant="dark" onClick={() => removeDetailCoords(dettaglio.id)} />
            )}
            <Button
              leadingIcon="edit"
              variant="light"
              size="sm"
              onClick={(e) => {
                e.stopPropagation()
                editDetail(dettaglio)
              }}
            />
            <Button
              leadingIcon="delete"
              variant="light"
              size="sm"
              onClick={(e) => {
                e.stopPropagation()
                deleteDetail(dettaglio)
              }}
            />
          </div>
        </div>
        <Collapse in={detail === dettaglio.id}>
          <div>
            <div dangerouslySetInnerHTML={{ __html: dettaglio.descrizione }}></div>
          </div>
        </Collapse>
      </div>
    </div>
  )
}
